/**
 * Pure selectors: live overlay doc → everything the badges and the panel
 * Live section render. No React, no fetching — testable against plain
 * objects (LIVEINFRA.md §3.1/§3.2 wording lives HERE so tests can pin it).
 *
 * Honesty laws enforced in this module:
 *  - every number states its source and age ("3 configured on Render (12s
 *    ago)", "declared 3 in render.yaml") — gating law 3;
 *  - "configured", never "running" (running counts are Slice 3 metrics);
 *  - freshness: a badge renders normally only while the sample is younger
 *    than 2× the source's poll interval; past that it dims and the tooltip
 *    reads "stale — last sample 4m ago" (§3.1).
 */

import type {
  LiveDeploy,
  LiveEnvOverlay,
  LiveOverlay,
  LiveQueueState,
  LiveRedisState,
  LiveServiceState,
  LiveStoreState,
  ViewEnvironment,
} from './types';
import { viewEnvironmentsOf } from './types';

/** Render collector cadence (§2: every 60s idle). */
export const RENDER_POLL_MS = 60_000;
/** Client-side overlay poll (§2 transport: 5s band cadence). */
export const CLIENT_POLL_MS = 5_000;
/** Live-mode overlay poll (§2 / DECISIONS #48f: 2s with ?mode=live). */
export const LIVE_MODE_POLL_MS = 2_000;
/** Redis collector cadence (#48f: 15s idle). */
export const REDIS_POLL_MS = 15_000;
/** Freshness rule (§3.1): younger than 2× the poll interval or dim. */
export const RENDER_STALE_AFTER_MS = 2 * RENDER_POLL_MS;
export const REDIS_STALE_AFTER_MS = 2 * REDIS_POLL_MS;

// ---------------------------------------------------------------------------
// Connection state
// ---------------------------------------------------------------------------

export type RenderConnection = 'off' | 'ok' | 'stale' | 'needs_key';

/**
 * Is any live source connected at all? Drives whether the client keeps
 * polling the overlay (attention-bounded on our side too) and whether the
 * disconnected copy renders.
 */
export function hasConnectedSource(overlay: LiveOverlay | null | undefined): boolean {
  if (overlay === null || overlay === undefined) return false;
  const { render, redis } = overlay.sources;
  return (
    (render !== undefined && render.status !== 'off') ||
    (redis !== undefined && redis.status !== 'off')
  );
}

/** The Render source's connection state ('off' when absent/disconnected). */
export function renderConnection(overlay: LiveOverlay | null | undefined): RenderConnection {
  const render = overlay?.sources['render'];
  if (render === undefined || render.status === 'off') return 'off';
  // A row with no key on file is not connected in any sense the reader
  // cares about: nothing will ever be read again until they paste one.
  if (render.api_key_present === false) return 'needs_key';
  return render.status === 'stale' ? 'stale' : 'ok';
}

/**
 * The Redis source's connection state — the top-level `sources.redis` chip
 * entry exists when ≥1 environment is connected (status = best across envs,
 * DECISIONS #48c); 'off' when absent/disconnected.
 */
export function redisConnection(overlay: LiveOverlay | null | undefined): RenderConnection {
  const redis = overlay?.sources['redis'];
  if (redis === undefined || redis.status === 'off') return 'off';
  return redis.status === 'stale' ? 'stale' : 'ok';
}

// ---------------------------------------------------------------------------
// Age formatting — "12s", "4m", "2h"
// ---------------------------------------------------------------------------

export function formatAgeSeconds(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h`;
}

/** Seconds since an ISO timestamp; null when missing/unparseable. */
export function ageSecondsOf(iso: string | null | undefined, nowMs: number): number | null {
  if (iso === null || iso === undefined || iso.length === 0) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((nowMs - t) / 1000));
}

// ---------------------------------------------------------------------------
// Deploy status classification (field values verified against the CLI at
// runtime by the server; the client classifies defensively)
// ---------------------------------------------------------------------------

export type DeployState = 'in_flight' | 'failed' | 'ok';

export function classifyDeployStatus(status: string): DeployState {
  const s = status.toLowerCase();
  if (/(fail|error)/.test(s)) return 'failed';
  if (/(progress|building|deploying|queued|created|pending|started)/.test(s)) return 'in_flight';
  return 'ok';
}

// ---------------------------------------------------------------------------
// Per-service live view
// ---------------------------------------------------------------------------

export interface ServiceLive {
  /** Render's scaling setting — the badge says "configured", never "running" */
  instancesConfigured: number | null;
  /** render.yaml numInstances, as the overlay reports it */
  instancesDeclared: number | null;
  /** instances_configured ≠ instances_declared (both known) */
  drift: boolean;
  /** service status as Render reports it (live / suspended / …) */
  status: string | null;
  deploy: LiveDeploy | null;
  deployState: DeployState | null;
  /** age of the Render sample (last_ok_at, falling back to sampled_at) */
  ageSeconds: number | null;
  /** source says stale, or the sample is older than 2× the poll interval */
  stale: boolean;
  environment: string;
}

function liveOfRow(
  overlay: LiveOverlay,
  connection: RenderConnection,
  row: LiveServiceState,
  nowMs: number,
): ServiceLive {
  const ageSeconds =
    ageSecondsOf(overlay.sources['render']?.last_ok_at, nowMs) ??
    ageSecondsOf(overlay.sampled_at, nowMs);
  const stale =
    connection === 'stale' ||
    ageSeconds === null ||
    ageSeconds * 1000 > RENDER_STALE_AFTER_MS;
  const configured = row.instances_configured;
  const declared = row.instances_declared;
  return {
    instancesConfigured: configured,
    instancesDeclared: declared,
    drift: configured !== null && declared !== null && configured !== declared,
    status: row.status !== null && row.status.length > 0 ? row.status : null,
    deploy: row.deploy,
    deployState:
      row.deploy !== null && row.deploy.status !== null
        ? classifyDeployStatus(row.deploy.status)
        : null,
    ageSeconds,
    stale,
    // the row's OWN env when the server tags it (#47); the overlay's default
    // view env covers pre-amendment payloads
    environment: row.environment ?? overlay.environment,
  };
}

/**
 * The live view for one `svc:` entity — ENTITY TRUTH from the all-env union
 * (the detail panel: an entity is inherently one env's service). Null when
 * no Render source is connected or the overlay has no row for it (band
 * pixel-identical, §3.1).
 */
export function serviceLive(
  overlay: LiveOverlay | null | undefined,
  entityId: string,
  nowMs: number,
): ServiceLive | null {
  if (overlay === null || overlay === undefined) return null;
  const connection = renderConnection(overlay);
  if (connection === 'off') return null;
  const row = overlay.services[entityId];
  if (row === undefined) return null;
  return liveOfRow(overlay, connection, row, nowMs);
}

/**
 * One view environment's slice of the overlay (#47) — null when the server
 * predates the amendment (callers fall back to the union).
 */
export function envSlice(
  overlay: LiveOverlay | null | undefined,
  env: ViewEnvironment,
): LiveEnvOverlay | null {
  const buckets = overlay?.environments;
  if (buckets === undefined) return null;
  return buckets[env] ?? { services: {}, unmatched: [], queues: {}, redis: null };
}

/**
 * The live view for one `svc:` entity UNDER A VIEW ENVIRONMENT — what the
 * band/spotlight badges render, so "badges follow the switcher": a prod
 * service shows nothing while staging is viewed; an env-agnostic service
 * (no name suffix) shows under every env. Falls back to entity truth when
 * the server has no per-env buckets yet.
 */
export function serviceLiveForEnv(
  overlay: LiveOverlay | null | undefined,
  entityId: string,
  env: ViewEnvironment,
  nowMs: number,
): ServiceLive | null {
  if (overlay === null || overlay === undefined) return null;
  const slice = envSlice(overlay, env);
  if (slice === null) return serviceLive(overlay, entityId, nowMs);
  const connection = renderConnection(overlay);
  if (connection === 'off') return null;
  const row = slice.services[entityId];
  if (row === undefined) return null;
  return liveOfRow(overlay, connection, row, nowMs);
}

// ---------------------------------------------------------------------------
// The exact §3.1/§3.2 wording (tests pin these)
// ---------------------------------------------------------------------------

/** Tooltip/caption: "3 configured on Render (12s ago)" — never "running". */
export function configuredCaption(live: ServiceLive): string | null {
  if (live.instancesConfigured === null) return null;
  const age = live.ageSeconds !== null ? formatAgeSeconds(live.ageSeconds) : '?';
  return `${live.instancesConfigured} configured on Render (${age} ago)`;
}

/** Spotlight caption line: "3 configured on Render · 12s ago". */
export function configuredLine(live: ServiceLive): string | null {
  if (live.instancesConfigured === null) return null;
  const age = live.ageSeconds !== null ? formatAgeSeconds(live.ageSeconds) : '?';
  return `${live.instancesConfigured} configured on Render · ${age} ago`;
}

/** Freshness treatment tooltip: "stale — last sample 4m ago". */
export function staleCaption(live: ServiceLive): string {
  const age = live.ageSeconds !== null ? formatAgeSeconds(live.ageSeconds) : '?';
  return `stale · last sample ${age} ago`;
}

/** Drift badge: "yaml declares 2, Render is configured for 3". */
export function driftCaption(live: ServiceLive): string | null {
  if (!live.drift || live.instancesDeclared === null || live.instancesConfigured === null) {
    return null;
  }
  return `yaml declares ${live.instancesDeclared}, Render is configured for ${live.instancesConfigured}`;
}

/**
 * The panel Live section headline: "3 instances × concurrency 1" — the
 * live-confirmed instance count bold in the UI; concurrency is manifest
 * truth (entity attrs), omitted when the extractor hasn't captured it.
 */
export function instancesHeadline(live: ServiceLive, concurrency: number | null): string | null {
  if (live.instancesConfigured === null) return null;
  const n = live.instancesConfigured;
  const base = `${n} ${n === 1 ? 'instance' : 'instances'}`;
  return concurrency !== null ? `${base} × concurrency ${concurrency}` : base;
}

/** Panel source line: "configured on Render, 12s ago". */
export function configuredSourceLine(live: ServiceLive): string {
  const age = live.ageSeconds !== null ? formatAgeSeconds(live.ageSeconds) : '?';
  return `configured on Render, ${age} ago`;
}

/** Panel manifest provenance: "declared 3 in render.yaml". */
export function declaredLine(live: ServiceLive): string | null {
  if (live.instancesDeclared === null) return null;
  return `declared ${live.instancesDeclared} in render.yaml`;
}

// ---------------------------------------------------------------------------
// Manifest attrs (extract-time truth in entity `attrs` — §2 static half)
// ---------------------------------------------------------------------------

function attrNumber(attrs: Record<string, unknown> | undefined, key: string): number | null {
  const v = attrs?.[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v);
  return null;
}

/** `--concurrency=N` parsed from startCommand at extract time. */
export function concurrencyOf(attrs: Record<string, unknown> | undefined): number | null {
  return attrNumber(attrs, 'concurrency');
}

/** numInstances as declared in render.yaml. */
export function declaredInstancesOf(attrs: Record<string, unknown> | undefined): number | null {
  return attrNumber(attrs, 'instances_declared');
}

// ---------------------------------------------------------------------------
// Slice 2 (DECISIONS #48): queues — per-environment truth, honesty captions
// ---------------------------------------------------------------------------

/** Client mirror of the server's #47 name-suffix env classification (null =
 * env-agnostic; such services count under every environment view). */
export function environmentOfName(name: string): ViewEnvironment | null {
  const m = /[-_]([a-z]+)$/.exec(name.toLowerCase());
  if (m === null) return null;
  const alias: Record<string, ViewEnvironment> = {
    prod: 'prod',
    production: 'prod',
    staging: 'staging',
    stage: 'staging',
    dev: 'dev',
    development: 'dev',
  };
  return alias[m[1]!] ?? null;
}

/** Exact chip copy for a partial (priority-sharded) depth — tests pin it. */
export const PARTIAL_DEPTH_COPY = 'priorities in use · depth is partial';

export interface QueueLive {
  depth: number | null;
  /** the ring buffer ([epoch_seconds, depth], ≤240) feeding the sparkline */
  samples: Array<[number, number]>;
  /** observed from depth deltas; negative = draining */
  ratePerMin: number | null;
  prioritiesDetected: boolean;
  /** the env this depth belongs to — a queue number is never env-less */
  env: ViewEnvironment;
  /** broker-wide in-flight for this env's Redis — all queues, never per queue */
  inflightTotal: number | null;
  ageSeconds: number | null;
  stale: boolean;
}

/**
 * One queue's live view UNDER A VIEW ENVIRONMENT — the only place queue
 * numbers exist (DECISIONS #48c: env buckets only; no union row because no
 * honest union number exists). Null when that env has no connected redis
 * source or no row for this queue — the chip stays pixel-identical.
 */
export function queueLiveForEnv(
  overlay: LiveOverlay | null | undefined,
  entityId: string,
  env: ViewEnvironment,
  nowMs: number,
): QueueLive | null {
  if (overlay === null || overlay === undefined) return null;
  const bucket = overlay.environments?.[env];
  if (bucket === undefined || bucket.redis === null || bucket.redis === undefined) return null;
  const row: LiveQueueState | undefined = bucket.queues?.[entityId];
  if (row === undefined) return null;
  const ageSeconds =
    ageSecondsOf(bucket.redis.last_ok_at, nowMs) ?? ageSecondsOf(overlay.sampled_at, nowMs);
  const stale =
    bucket.redis.status !== 'ok' ||
    ageSeconds === null ||
    ageSeconds * 1000 > REDIS_STALE_AFTER_MS;
  return {
    depth: row.depth,
    samples: row.samples,
    ratePerMin: row.rate_per_min,
    prioritiesDetected: row.priorities_detected,
    env,
    inflightTotal: bucket.redis.inflight_total,
    ageSeconds,
    stale,
  };
}

/** The chip's appended count text: "12 · prod" (§3.1 "pipelines · 12 · prod"). */
export function queueCountText(live: QueueLive): string | null {
  if (live.depth === null) return null;
  return `${live.depth} · ${live.env}`;
}

/** Chip tooltip: honest source + age (+ the partial-depth warning). */
export function queueChipTitle(label: string, live: QueueLive): string {
  const age = live.ageSeconds !== null ? formatAgeSeconds(live.ageSeconds) : '?';
  const head =
    live.depth !== null
      ? `${label} · ${live.depth} queued · ${live.env}`
      : `${label} · depth unknown · ${live.env}`;
  const freshness = live.stale
    ? `stale · last sample ${age} ago`
    : `live from ${live.env} Redis (${age} ago)`;
  const parts = [head, freshness];
  if (live.prioritiesDetected) parts.push(PARTIAL_DEPTH_COPY);
  return parts.join(' · ');
}

/** Panel/chip rate caption: "draining ~3.5/min" (observed, never simulated). */
export function drainingCaption(live: QueueLive): string | null {
  if (live.ratePerMin === null) return null;
  const magnitude = Math.round(Math.abs(live.ratePerMin) * 10) / 10;
  if (live.ratePerMin < 0) return `draining ~${magnitude}/min`;
  if (live.ratePerMin > 0) return `growing ~${magnitude}/min`;
  return 'holding steady';
}

/** The broker line, labeled the only honest way (§2): all queues, one env. */
export function inflightCaption(live: QueueLive): string | null {
  if (live.inflightTotal === null) return null;
  return `${live.inflightTotal} in flight (all queues)`;
}

// ---------------------------------------------------------------------------
// Consumers + slots (view-env filtered — a prod depth is never divided
// across dev workers, DECISIONS #47/#48g)
// ---------------------------------------------------------------------------

export interface QueueConsumer {
  entityId: string;
  /** service name (entity id minus svc:) */
  name: string;
  instancesConfigured: number | null;
  concurrency: number | null;
  /** (configured ?? declared ?? 1) × (concurrency ?? 1) */
  slots: number;
}

interface ConsumesEdge {
  from: string;
  to: string;
  kind: string;
}

/**
 * The services consuming one queue IN the view environment (env-agnostic
 * names count everywhere), with their slot math. `attrsOf` supplies the
 * manifest attrs (concurrency, instances_declared); live configured counts
 * come from the overlay's env bucket (falling back to the all-env union).
 */
export function queueConsumersForEnv(
  entityEdges: ReadonlyArray<ConsumesEdge>,
  attrsOf: (entityId: string) => Record<string, unknown> | undefined,
  overlay: LiveOverlay | null | undefined,
  queueId: string,
  env: ViewEnvironment,
): QueueConsumer[] {
  const consumers: QueueConsumer[] = [];
  for (const edge of entityEdges) {
    if (edge.kind !== 'consumes' || edge.to !== queueId || !edge.from.startsWith('svc:')) {
      continue;
    }
    const name = edge.from.slice('svc:'.length);
    const rowEnv = environmentOfName(name);
    if (rowEnv !== null && rowEnv !== env) continue;
    const liveRow =
      overlay?.environments?.[env]?.services?.[edge.from] ?? overlay?.services?.[edge.from];
    const attrs = attrsOf(edge.from);
    const configured = liveRow?.instances_configured ?? null;
    const declared = liveRow?.instances_declared ?? declaredInstancesOf(attrs);
    const concurrency = concurrencyOf(attrs);
    consumers.push({
      entityId: edge.from,
      name,
      instancesConfigured: configured,
      concurrency,
      slots: Math.max(1, (configured ?? declared ?? 1) * (concurrency ?? 1)),
    });
  }
  return consumers.sort((a, b) => a.entityId.localeCompare(b.entityId));
}

export function totalSlots(consumers: ReadonlyArray<QueueConsumer>): number {
  return consumers.reduce((sum, c) => sum + c.slots, 0);
}

/** Panel consumer line: "consumed by curium-pipelines-prod (1 slot)". */
export function consumerLine(consumers: ReadonlyArray<QueueConsumer>): string | null {
  if (consumers.length === 0) return null;
  const names = consumers.map((c) => c.name).join(', ');
  const slots = totalSlots(consumers);
  return `consumed by ${names} (${slots} slot${slots === 1 ? '' : 's'})`;
}

/**
 * The orphaned-queue LIVE alarm (§3.1): work is waiting AND the view env
 * PROVABLY has nobody to take it — either no consumer service classifies to
 * this env at all, or Render reports 0 configured instances for every one
 * that does. Unknown counts (Render not connected) never alarm: the alarm
 * only fires on facts.
 */
export function orphanedQueueLiveAlarm(
  live: QueueLive,
  consumers: ReadonlyArray<QueueConsumer>,
): string | null {
  if (live.depth === null || live.depth <= 0) return null;
  if (consumers.length === 0) {
    return `${live.depth} waiting · no consumer in ${live.env}`;
  }
  const counts = consumers.map((c) => c.instancesConfigured);
  if (counts.every((n) => n === 0)) {
    return `${live.depth} waiting · 0 instances configured in ${live.env}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Env-switcher legibility (owner bug report: switching must be VISIBLE)
// ---------------------------------------------------------------------------

export interface EnvGlance {
  /** live service rows classified to this env (env-agnostic rows included) */
  services: number;
  deploying: number;
  deployFailed: number;
  /** queue rows with a known depth in this env; 0 when redis is off here */
  queues: number;
  /** total queued across this env's queues (honest: one env, one Redis) */
  queued: number | null;
}

/** One env's at-a-glance numbers for the switcher chips + summary line. */
export function envGlance(
  overlay: LiveOverlay | null | undefined,
  env: ViewEnvironment,
): EnvGlance | null {
  const bucket = overlay?.environments?.[env];
  if (overlay === null || overlay === undefined || bucket === undefined) return null;
  const rows = Object.values(bucket.services ?? {});
  let deploying = 0;
  let deployFailed = 0;
  for (const row of rows) {
    if (row.deploy === null || row.deploy.status === null) continue;
    const state = classifyDeployStatus(row.deploy.status);
    if (state === 'in_flight') deploying += 1;
    if (state === 'failed') deployFailed += 1;
  }
  const queueRows = Object.values(bucket.queues ?? {});
  const depths = queueRows.map((q) => q.depth).filter((d): d is number => d !== null);
  return {
    services: rows.length,
    deploying,
    deployFailed,
    queues: queueRows.length,
    queued: bucket.redis !== null && bucket.redis !== undefined ? depths.reduce((a, b) => a + b, 0) : null,
  };
}

/**
 * The one-line summary under the switcher — it CHANGES with the selection,
 * so switching envs is legible even when the badges look alike.
 */
export function envSummaryLine(env: ViewEnvironment, glance: EnvGlance | null): string {
  if (glance === null || (glance.services === 0 && glance.queues === 0)) {
    return `Viewing ${env}: no live rows in this environment`;
  }
  const parts = [`${glance.services} service${glance.services === 1 ? '' : 's'}`];
  if (glance.deploying > 0) parts.push(`${glance.deploying} deploying`);
  if (glance.deployFailed > 0) parts.push(`${glance.deployFailed} deploy failed`);
  if (glance.queued !== null) parts.push(`${glance.queued} queued`);
  return `Viewing ${env}: ${parts.join(', ')}`;
}

// ---------------------------------------------------------------------------
// The stores Render lists (DECISIONS #91)
// ---------------------------------------------------------------------------

/** Every store Render lists, all environments, in view-env order then by name. */
export function storesListed(overlay: LiveOverlay | null | undefined): LiveStoreState[] {
  const rows = overlay?.stores ?? [];
  const known = viewEnvironmentsOf(overlay);
  const rank = (env: string | null): number => {
    const i = env === null ? -1 : known.indexOf(env);
    return i === -1 ? known.length : i;
  };
  return [...rows].sort(
    (a, b) => rank(a.environment) - rank(b.environment) || a.name.localeCompare(b.name),
  );
}

/** One environment's Redis broker state, or null when none is read there. */
export function redisStateForEnv(
  overlay: LiveOverlay | null | undefined,
  env: ViewEnvironment,
): LiveRedisState | null {
  const state = overlay?.environments?.[env]?.redis ?? null;
  return state !== null && state.status !== 'off' ? state : null;
}

/** The store a connection string would be for: name and environment. */
export interface StoreTarget {
  name: string;
  environment: ViewEnvironment | null;
}

/**
 * The Redis store Render lists for one environment, if any: the sheet opens
 * on it with the environment fixed, because the discovery already says
 * which environment the store serves.
 */
export function redisStoreForEnv(
  overlay: LiveOverlay | null | undefined,
  env: ViewEnvironment,
): StoreTarget | null {
  const store = storesListed(overlay).find((s) => s.kind === 'redis' && s.environment === env);
  return store === undefined ? null : { name: store.name, environment: env };
}

/** "Key Value (Redis)", "Postgres", else the kind as Render names it. */
export function storeKindLabel(kind: string): string {
  if (kind === 'redis') return 'Key Value (Redis)';
  if (kind === 'postgres') return 'Postgres';
  return kind;
}

/** What Render says about a store, in one line: kind, plan, version, status. */
export function storeFactsLine(store: LiveStoreState): string {
  const bits = [store.label ?? storeKindLabel(store.kind)];
  if (store.plan !== null) bits.push(`${store.plan} plan`);
  if (store.version !== null) bits.push(`v${store.version}`);
  if (store.status !== null) bits.push(store.status);
  if (store.entity_id === null) bits.push('not in your manifest');
  return bits.join(' · ');
}
